import { useCallback, useEffect, useState } from "react";
import { api } from "@/api/client";
import { PlatformBadge } from "@/components/PlatformLogo";
import { SectionHeader } from "@/components/SectionHeader";
import { Skeleton } from "@/components/Skeleton";
import type { Platform, PortalBalance, PortalBalances } from "@/types";

/** Render a portal balance as a currency-prefixed amount (₾ / $ fallback to
 *  the raw code). Shared with the Team list column. */
export function formatBalance(b: PortalBalance): string {
  if (b.balance == null) return "—";
  const sym =
    b.currency === "USD"
      ? "$"
      : b.currency === "GEL"
        ? "₾"
        : b.currency
          ? b.currency + " "
          : "";
  return `${sym}${Number(b.balance).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Map a balance error code to a short human label. */
export function balanceErrorLabel(error: string | null): string {
  switch (error) {
    case "no_credentials":
      return "No saved credentials";
    case "login_failed":
      return "Login failed — check credentials";
    case "portal_unreachable":
      return "Couldn't reach portal — try Refresh";
    case "balance_field_not_found":
      return "Balance unavailable";
    default:
      return "Unavailable";
  }
}

function BalanceRow({
  platform,
  data,
  loading,
}: {
  platform: Platform;
  data: PortalBalance | null;
  loading: boolean;
}) {
  return (
    <div className="flex items-center justify-between px-5 py-3.5 hover:bg-white/[0.015] transition-colors">
      <PlatformBadge platform={platform} size="sm" />
      {loading || data == null ? (
        <Skeleton className="h-5 w-20" />
      ) : data.ok ? (
        <span className="text-[15px] font-semibold tabular-nums text-slate-100">
          {formatBalance(data)}
        </span>
      ) : (
        <span className="text-[11.5px] text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-md px-2 py-0.5">
          {balanceErrorLabel(data.error)}
        </span>
      )}
    </div>
  );
}

/** Live MyHome + SS wallet balance for one user. Auto-fetches on mount and
 *  exposes a manual Refresh that bypasses the backend's short-TTL cache.
 *  Self-readable on Profile; manager+ readable on EmployeeDetail (the
 *  endpoint enforces visibility). */
export function PortalBalanceCard({ userId }: { userId: string }) {
  const [data, setData] = useState<PortalBalances | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(
    async (force: boolean) => {
      setLoading(true);
      setErr(null);
      try {
        const res = await api<PortalBalances>(
          `/api/team/${userId}/portal-balances${force ? "?refresh=true" : ""}`,
        );
        setData(res);
      } catch {
        setErr("Couldn't load balances.");
      } finally {
        setLoading(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  return (
    <div className="card overflow-hidden">
      <SectionHeader
        title="Portal balances"
        subtitle="Live wallet balance from MyHome + SS.ge"
        right={
          <button
            type="button"
            onClick={() => void load(true)}
            disabled={loading}
            className="text-[12px] text-slate-300 hover:text-slate-100 disabled:opacity-40 transition-colors"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        }
      />
      {err ? (
        <div className="px-5 py-4 text-[12.5px] text-rose-400">{err}</div>
      ) : (
        <div className="divide-y divide-white/5">
          <BalanceRow
            platform="myhome"
            data={data?.myhome ?? null}
            loading={loading && data == null}
          />
          <BalanceRow
            platform="ss"
            data={data?.ss ?? null}
            loading={loading && data == null}
          />
        </div>
      )}
    </div>
  );
}
